import { useRef, useState, useEffect } from "react";
import { useApi } from "../../hooks/use-api";
import { useSingleton } from "../../hooks/use-singleton";
import {
  API_SUBJECTS,
  apiStudentStudys,
  API_AI_CONTENT_CHAPTERS,
  API_AI_CONCEPTS_DROPDOWN_STUDENTS,
  APISHORTMULTI,
  APILONGMULTI,
  API_MEDIUMS,
  APIRANDOMMULTI,
  apiMyAssestsSearch,
  API_GENERATE_VIDEO,
} from "../../utils/api-routes";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../hooks/use-notify";

const useHandler = () => {
  const { get, post } = useApi();
  const { toggleNotify } = useNotify();
  const { mail, user, role, section } = useSingleton();
  const navigate = useNavigate();
  const DURATION_OPTIONS = ["Secs", "Mins", "Hrs"];

  /* ===================== UI STATES ===================== */
  const [togglebtn, setToggleBtn] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [selectedMedium, setSelectedMedium] = useState("");
  const [conceptText, setConceptText] = useState({});
  const [preferredType, setPreferredType] = useState("audio");
  const [selectMode, setSelectMode] = useState("random");
  const [isLoading, setIsLoading] = useState(false);
  const [classdetails, setClassDetails] = useState({});
  const [duration, setDuration] = useState("");
  const [conceptOptions, setConceptOptions] = useState([]);

  const [durationType, setDurationType] = useState(
    role === "guest" ? "Secs" : "Mins",
  );
  const [durationDropdown, setDurationDropdown] = useState(false);

  const filteredDurationOptions =
    role === "guest" ? ["Secs"] : DURATION_OPTIONS;

  useEffect(() => {
    if (role === "guest") {
      setDurationType("Secs");
    }
  }, [role]);

  const handleDurationChange = (val) => {
    if (role === "guest") {
      if (parseInt(val) > 30) {
        setDuration("30");
        return;
      }
    }
    setDuration(val);
  };
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [assetPopupOpen, setAssetPopupOpen] = useState(false);
  const [popupData, setPopupData] = useState({
    open: false,
    title: "",
    description: "",
    buttonText: "OK",
    assetId: null,
  });

  const languageRef = useRef();
  const subjectRef = useRef();
  const chapterRef = useRef();
  const mediumRef = useRef(null);

  /* ===================== DATA STATES ===================== */

  // 👉 ALL SUBJECT OPTIONS FROM API
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [chapterOptions, setChapterOptions] = useState({});
  const [mediums, setMediums] = useState([]);

  // 👉 USER SELECTED SUBJECTS (UI ROWS)
  const [subjects, setSubjects] = useState([]);

  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);

  /* ===================== API CALLS ===================== */

  const getUserStudiesDetails = async () => {
    try {
      const res = await get(apiStudentStudys(mail));

      if (res?.data?.data) {
        setClassDetails(res?.data);

        const payload = {
          board: res.data.data.board,
          clas: res.data.data.class,
          medium: res.data.data.medium, // ✅ use directly
        };

        getMediumDetails(payload);
        getSubjectDetails(payload); // ✅ CALL HERE DIRECTLY
      }
    } catch (e) {
      console.log("Error getting study details", e);
    }
  };

  const getMediumDetails = async (payload) => {
    setIsLoading(true);
    try {
      const res = await post(API_MEDIUMS, payload);
      if (res) {
        setMediums(res?.data?.mediums);
        setIsLoading(false);
      } else {
        alert(res?.message);
      }
    } catch (e) {
      setIsLoading(false);
      console.log("Error on getting Subject Details", e);
    }
  };

  const getSubjectDetails = async (payload) => {
    try {
      const res = await post(API_SUBJECTS, payload);
      if (res?.data?.subjects) {
        setSubjectOptions(res.data.subjects); // ✅ OPTIONS ONLY
      }
    } catch (e) {
      console.log("Error getting subjects", e);
    }
  };

  const getChapterDetails = async ({
    subject,
    board,
    clas,
    medium,
    subjectId,
  }) => {
    try {
      const res = await post(API_AI_CONTENT_CHAPTERS, {
        subject,
        board,
        clas,
        medium: user?.data?.medium || classdetails?.data?.medium,
      });

      if (res?.data?.chapters) {
        setChapterOptions((prev) => ({
          ...prev,
          [subjectId]: res.data.chapters, // ✅ ONLY OPTIONS
        }));
      }
    } catch (e) {
      console.log("Error getting chapters", e);
    }
  };

  useEffect(() => {
    getUserStudiesDetails();
  }, []);

  const myAssetsApiWithFilters = async (currentFilters) => {
    try {
      const res = await get(apiMyAssestsSearch(mail, currentFilters.search));

      return res; // ✅ IMPORTANT
    } catch (e) {
      console.log("ERROR", e);
    }
  };

  const handleExistingAssets = async (assetId) => {
    try {
      const res = await myAssetsApiWithFilters({ search: assetId });

      const assets = res?.data?.assets || [];
      setFilteredAssets(assets);

      return assets; // ✅ return
    } catch (e) {
      console.log("Error fetching assets", e);
    }
  };

  const formattedDetails = () => {
    const formattedSubjects = subjects?.reduce((acc, subject) => {
      if (!subject?.name) return acc;

      acc[subject.name] = subject?.chapters?.reduce((chapterAcc, chapter) => {
        if (!chapter?.name || !chapter?.concepts?.length) return chapterAcc;

        chapterAcc[chapter.name] = chapter?.concepts;
        return chapterAcc;
      }, {});

      return acc;
    }, {});

    return {
      mail,
      board: user?.data?.board,
      clas: user?.data?.class,
      language: selectedLanguage,
      subjects: formattedSubjects,
      video: preferredType === "video" ? true : false,
      audio: preferredType === "audio" ? true : false,
      medium: user?.data?.medium,
      section,
    };
  };

  const generateVideoContent = async () => {
    try {
      const payload = {
        mail,
        board: user?.data?.board,
        clas: user?.data?.class,
        medium: user?.data?.medium || classdetails?.data?.medium,
        subject: subjects?.[0]?.name || "",
        chapter: subjects?.[0]?.chapters?.[0]?.name || "",
        language: selectedLanguage,
        section,
      };

      const res = await post(API_GENERATE_VIDEO, payload);

      if (res) {
        setPopupData({
          open: true,
          title: "Video Generation Started 🎬",
          description:
            "Your video is being generated. You’ll be notified once it's ready.",
          buttonText: "OK",
        });
      }
    } catch (e) {
      console.log("ERROR IN VIDEO API", e);
      toggleNotify({
        type: "error",
        title: "Error",
        description:
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          "Failed to start video generation",
      });
    }
  };

  const generateAIContent = async () => {
    const payload = formattedDetails();
    try {
      const res = await post(APISHORTMULTI, payload);
      if (res) {
        if (res?.data?.status === "ALREADY_EXISTS") {
          const assetId = res?.data?.existing_assets?.[0]?.asset_id;

          setPopupData({
            open: true,
            title: "Already Exists",
            description: res?.data?.message || "Content already generated",
            buttonText: "OK",
            assetId: assetId,
          });
        } else {
          setPopupData({
            open: true,
            title: "Hang Tight!",
            description:
              "We’re working on your content right now. You don’t have to stay on this page — we’ll send you an email once it's ready.",
            buttonText: "Go to Assets Page",
          });
        }
      }
    } catch (e) {
      console.log("ERROR IS COMES FROM GENEREATEAICONTENT API", e);
    }
  };

  const aiGenerate = () => generateAIContent();

  const formattedLongDetails = () => {
    const formattedSubjects = subjects?.reduce((acc, subject) => {
      if (!subject?.name || !subject?.chapters?.length) return acc;

      const chapterList = subject.chapters
        ?.filter((chapter) => chapter?.name)
        ?.map((chapter) => chapter.name);

      if (chapterList?.length) {
        acc[subject.name] = chapterList;
      }

      return acc;
    }, {});

    return {
      mail,
      board: user?.data?.board,
      clas: user?.data?.class,
      language: selectedLanguage,
      subjects: formattedSubjects,
      video: preferredType === "video",
      audio: preferredType === "audio",
      medium: user?.data?.medium,
      section,
    };
  };

  const generateAILongContent = async () => {
    const payload = formattedLongDetails();
    try {
      const res = await post(APILONGMULTI, payload);
      if (res) {
        if (res?.data?.status === "ALREADY_EXISTS") {
          const assetId = res?.data?.existing_assets?.[0]?.asset_id; // ✅ FIX
          console.log(assetId, "asetid2");
          setPopupData({
            open: true,
            title: "Already Exists",
            description: res?.data?.message || "Content already generated",
            buttonText: "OK",
            assetId: assetId,
          });
        } else {
          setPopupData({
            open: true,
            title: "Hang Tight!",
            description:
              "We’re working on your content right now. You don’t have to stay on this page — we’ll send you an email as soon as it’s done. In the meantime, check out your previously generated assets",
            buttonText: "Go to Assets Page",
          });
        }
      }
    } catch (e) {
      console.log("ERROR IS COMES FROM GENERATELONGCONTENT", e);
    }
  };

  const aiLongGenerate = () => generateAILongContent();

  const generateAIRandomContent = async () => {
    const payload = {
      mail,
      board: user?.data?.board,
      clas: user?.data?.class,
      medium: user?.data?.medium,
      subject: subjects?.[0]?.name || "",
      // duration: duration, // state you create for duration input
      duration: `${duration} ${durationType}`,
      language: selectedLanguage,
      video: preferredType === "video",
      section,
    };

    try {
      const res = await post(APIRANDOMMULTI, payload);
      if (res) {
        if (res?.data?.status === "ALREADY_EXISTS") {
          const assetId = res?.data?.existing_assets?.[0]?.asset_id;

          setPopupData({
            open: true,
            title: "Already Exists",
            description: res?.data?.message || "Content already generated",
            buttonText: "OK",
            assetId: assetId,
          });
        } else {
          setPopupData({
            open: true,
            title: "Hang Tight!",
            description:
              "We’re generating your custom content. You can leave this page — we’ll notify you once it's ready.",
            buttonText: "Go to Assets Page",
          });
        }
      }
    } catch (e) {
      console.log("ERROR COMES FROM AI GENERATE RANDOM CONTENT", e);
    }
  };

  const aiRandomGenerate = () => generateAIRandomContent();

  /* ===================== SUBJECT ACTIONS ===================== */

  const addSubject = () => {
    if (preferredType === "video" && subjects.length >= 1) return;
    if (subjects.length >= 3) return;
    if (selectMode === "random" && subjects.length >= 1) return;

    setSubjects((prev) => [
      ...prev,
      {
        id: Date.now(),
        name: "", // EMPTY INITIALLY
        chapters: [],
        selectedChapterId: null,
      },
    ]);
  };

  const deleteSubject = (id) => {
    setSubjects((prev) => prev.filter((s) => s.id !== id));
  };

  /* ===================== CHAPTER ACTIONS ===================== */

  const addChapters = (subjectId) => {
    setSubjects((prev) =>
      prev.map((s) => {
        if (preferredType === "video" && s.chapters.length >= 1) return s;
        if (s.id !== subjectId) return s;
        if (s.chapters.length >= 2) return s;

        return {
          ...s,
          chapters: [
            ...s.chapters,
            {
              id: Date.now(),
              name: "",
              concepts: [],
            },
          ],
        };
      }),
    );
  };

  const deleteChapter = (subjectId, chapterId) => {
    setSubjects((prev) =>
      prev.map((s) =>
        s.id === subjectId
          ? {
              ...s,
              chapters: s.chapters.filter((c) => c.id !== chapterId),
              selectedChapterId:
                s.selectedChapterId === chapterId ? null : s.selectedChapterId,
            }
          : s,
      ),
    );
  };

  /* ===================== CONCEPT ACTIONS ===================== */

  const getConceptDetails = async (payload) => {
    try {
      const res = await post(API_AI_CONCEPTS_DROPDOWN_STUDENTS, payload);

      if (res?.data?.concepts) {
        setConceptOptions(res.data.concepts);
      }
    } catch (e) {
  toggleNotify({
    type: "error",
    title: "Error",
    description:
      e?.response?.data?.error || "Something went wrong",
  });
}
  };

  const addConcept = (subjectId, chapterId, concept) => {
    if (!concept?.trim()) return;

    setSubjects((prev) =>
      prev.map((s) =>
        s.id === subjectId
          ? {
              ...s,
              chapters: s.chapters.map((c) => {
                if (c.id !== chapterId) return c;

                const existing = c.concepts || [];

                // prevent duplicate
                if (existing.includes(concept)) return c;

                // Guest: replace existing concept (single select behavior)
                if (role === "guest") {
                  return {
                    ...c,
                    concepts: [concept.trim()],
                  };
                }

                // others: max 3 concepts
                if (existing.length >= 3) return c;

                return { ...c, concepts: [...existing, concept.trim()] };
              }),
            }
          : s,
      ),
    );
  };

  const deleteConcept = (subjectId, chapterId, index) => {
    setSubjects((prev) =>
      prev.map((s) =>
        s.id === subjectId
          ? {
              ...s,
              chapters: s.chapters.map((c) =>
                c.id === chapterId
                  ? {
                      ...c,
                      concepts: c.concepts.filter((_, i) => i !== index),
                    }
                  : c,
              ),
            }
          : s,
      ),
    );
  };

  /* ===================== UTILS ===================== */

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (languageRef.current?.contains(e.target)) return;
      setOpenDropdown(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const onToggleBtn = () => setToggleBtn((p) => !p);

  /* ===================== EXPORT ===================== */

  const onSelectSubject = ({ subjectId, subjectName }) => {
    // 1️⃣ Update subject
    setSubjects((prev) =>
      prev.map((s) =>
        s.id === subjectId
          ? {
              ...s,
              name: subjectName,
              chapters:
                preferredType === "video"
                  ? [
                      {
                        id: Date.now(),
                        name: "",
                        concepts: [],
                      },
                    ]
                  : selectMode !== "random"
                    ? [] // ❌ no chapters for custom mode
                    : [
                        {
                          id: Date.now(),
                          name: "",
                          concepts: [],
                        },
                      ],
            }
          : s,
      ),
    );

    // 2️⃣ Fetch chapters
    getChapterDetails({
      subject: subjectName,
      board: user?.data?.board,
      clas: user?.data?.class,
      medium: user?.data?.medium || classdetails?.data?.medium,
      subjectId,
    });

    // ❌ DO NOT open chapter dropdown automatically
    setOpenDropdown(null);
  };

  const onSelectChapter = ({ subjectId, chapterName }) => {
    setSubjects((prev) =>
      prev.map((s) => {
        if (s.id !== subjectId) return s;

        // ❌ Prevent duplicate
        if (s.chapters.some((c) => c.name === chapterName)) {
          return s;
        }

        if (s.chapters.length >= 2) return s;

        return {
          ...s,
          chapters: [
            ...s.chapters,
            {
              id: Date.now(),
              name: chapterName,
              concepts: [],
            },
          ],
        };
      }),
    );

    setOpenDropdown(null);
  };

 const resetForm = () => {
  setSubjects([
    {
      id: Date.now(),
      name: "",
      chapters: [],
      selectedChapterId: null,
    },
  ]);

  setChapterOptions({});
  setSelectedLanguage("");
};

  const handleGenerate = () => {
    // 🎥 VIDEO CASE
    if (preferredType === "video") {
      return generateVideoContent(); // ✅ CALL VIDEO API ONLY HERE
    }

    // 🔊 AUDIO / OTHER CASES
    if (selectMode === "short") return aiGenerate();
    if (selectMode === "long") return aiLongGenerate();
    if (selectMode === "random") return aiRandomGenerate();
  };

  useEffect(() => {
    // Reset all dependent states when mode changes

    setSubjects([]);
    setConceptText({});
    setSelectedSubject(null);
    setSelectedChapter(null);
    setChapterOptions({});

    // Reset duration only if NOT random
    if (selectMode !== "random") {
      setDuration("");
    }

    // Close dropdowns
    setOpenDropdown(null);
  }, [selectMode]);

  const isFormValid = () => {
    // ================= COMMON VALIDATION =================
    const userMedium = user?.data?.medium || classdetails?.data?.medium;
    if (!userMedium || !selectedLanguage) return false;

    // 🎥 VIDEO MODE VALIDATION
    if (preferredType === "video") {
      return (
        subjects?.length === 1 &&
        subjects?.[0]?.name &&
        subjects?.[0]?.chapters?.length === 1 &&
        subjects?.[0]?.chapters?.[0]?.name
      );
    }

    // ================= SHORT MODE =================
    if (selectMode === "short") {
      return (
        subjects?.length > 0 &&
        subjects.every(
          (subject) =>
            subject.name &&
            subject.chapters?.length > 0 &&
            subject.chapters.every(
              (chap) => chap.name && chap.concepts?.length > 0,
            ),
        )
      );
    }

    // ================= LONG MODE =================
    if (selectMode === "long") {
      return (
        subjects?.length > 0 &&
        subjects.every(
          (subject) =>
            subject.name &&
            subject.chapters?.length > 0 &&
            subject.chapters.every((chap) => chap.name),
        )
      );
    }

    // ================= RANDOM MODE =================
    if (selectMode === "random") {
      return (
        subjects?.length === 1 && // only 1 subject allowed
        subjects?.[0]?.name &&
        duration?.toString().trim() !== "" &&
        durationType
      );
    }

    return false;
  };

  return {
    DURATION_OPTIONS,
    user,
    role,
    isFormValid,
    togglebtn,
    subjects,
    subjectOptions,
    selectedSubject,
    openDropdown,
    selectedLanguage,
    conceptText,
    languageRef,
    subjectRef,
    chapterRef,
    chapterOptions,
    selectedChapter,
    preferredType,
    selectMode,
    selectedMedium,
    mediums,
    mediumRef,
    isLoading,
    duration,
    durationType,
    durationDropdown,

    setSelectedSubject,
    setOpenDropdown,
    setSelectedLanguage,
    setConceptText,
    setSubjects,
    setSelectedChapter,
    setPreferredType,
    setSelectMode,
    setSelectedMedium,
    setDuration,
    handleDurationChange,
    filteredDurationOptions,
    setDurationType,
    setDurationDropdown,
    generateVideoContent,
    getChapterDetails,
    onSelectChapter,
    onSelectSubject,
    onToggleBtn,
    addSubject,
    deleteSubject,
    addChapters,
    deleteChapter,
    addConcept,
    deleteConcept,
    aiGenerate,
    resetForm,
    aiLongGenerate,
    handleGenerate,
    navigate,
    popupData,
    setPopupData,
    handleExistingAssets,
    filteredAssets,
    setFilteredAssets,
    assetPopupOpen,
    setAssetPopupOpen,
    toggleNotify,
    conceptOptions,
    setConceptOptions,
    getConceptDetails,
  };
};

export default useHandler;
